import { useQueryClient } from 'react-query';

import { AppMessage, DomainPunycode } from '@/common/components';
import { useAppSocket, useIsSuccessful } from '@/common/hooks';
import { AppPaginationOptions, BaseQueryParams } from '@/common/interfaces';

import { DomainDeployStatus, DomainDeploymentGatewayKeys, DomainsQueryKeys, useGetAllDomainsQuery, useGetByIdDomainsQuery, useGetConfigDomainsQuery } from '@/modules/Domains';

interface Data {
  domainId: string;
  deployStatus: DomainDeployStatus;
  isDeployed: boolean;
  errorMessage: string | null;
  errorDescription: string | null;
}

interface Args {
  domainId?: string | null;
  domainName: string | null;
  pageParam?: AppPaginationOptions | null;
}

export const useUpdateDomainDeployStatus = ({ domainId, domainName, pageParam }: Args) => {
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();

  const isDomainDeploymentInfoSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetConfig, domainId] });
  const isDomainByIdRequestSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetOneById, domainId] });
  const isAllDomainsRequestSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetAll, pageParam] });

  useAppSocket<Data>(DomainDeploymentGatewayKeys.UpdateDeployStatus, ({ lastMessage }) => {
    const { deployStatus, domainId: id, isDeployed, errorMessage, errorDescription } = lastMessage;

    if (domainId) {
      if (isDomainDeploymentInfoSuccess && domainId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetConfigDomainsQuery>['data']
        >([DomainsQueryKeys.GetConfig, id], (data) => {
          return {
            ...data,
            isDeployed,
            deployStatus
          } as BaseQueryParams<typeof useGetConfigDomainsQuery>['data'];
        });
      }

      if (isDomainByIdRequestSuccess && domainId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetByIdDomainsQuery>['data']
        >([DomainsQueryKeys.GetOneById, id], (data) => {
          return {
            ...data,
            isDeployed,
            deployStatus,
            deployErrorMessage: errorMessage,
            deployErrorDescription: errorDescription
          } as BaseQueryParams<typeof useGetByIdDomainsQuery>['data'];
        });
      }

      if (deployStatus === DomainDeployStatus.Deployed && id === domainId) {
        appMessageApi.info(
          `Звʼязок з доменом '${ DomainPunycode.stringFormat(domainName ?? '') }' буде перевірено`,
          { key: `deploy-status-${ domainId }` }
        );
      }
    }

    if (isAllDomainsRequestSuccess) {
      queryClient.setQueryData<
        BaseQueryParams<typeof useGetAllDomainsQuery>['data']
      >([DomainsQueryKeys.GetAll, pageParam], (data) => {
        return {
          ...data,
          items: data?.items.some(el => el.id === id)
            ? data?.items.map((item) =>
              item.id === id
                ? {
                  ...item,
                  isDeployed,
                  deployStatus
                }
                : item
            ) ?? []
            : data?.items,
        } as BaseQueryParams<typeof useGetAllDomainsQuery>['data'];
      });
    }
  });
};
